import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import omitBy from 'lodash.omitby';

import { urlObjectToUriString } from '@restworld/utils-common';
import { Combobox } from '@restworld/ui-ds-base';

import useEmployerRestaurantNames from '../../restaurants/hooks/use-employer-restaurant-names';
import {
  employerSpontaneousCandidatesRestaurantFilterAtom,
  employerSpontaneousCandidatesShowHiddenFilterAtom,
} from '../employer-spontaneous-candidates.atoms';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';
import { Checkbox } from '@restworld/ui-ds-base';

export function ShowHiddenCandidatesFilter() {
  const { t } = useTranslation('spontaneous-candidates-page');
  const [showHidden, setShowHidden] = useAtom(employerSpontaneousCandidatesShowHiddenFilterAtom);
  const router = useV2Router();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowHidden(e.target.checked);

    router.push(
      urlObjectToUriString({
        pathname: router.pathname,
        query: {
          ...router.query,
          hidden: e.target.checked,
        },
      }),
      undefined,
      {
        shallow: true,
      }
    );
  };
  return (
    <div className="w-fit lg:w-full flex flex-row items-center">
      <Checkbox isChecked={showHidden} onChange={handleChange} title={t('show_hidden_candidates')} />
    </div>
  );
}

const RESTAURANT_FILTER_VISIBILITY_THRESHOLD = 2;

export function SpontaneousCandidateRestaurantFilter() {
  const { t } = useTranslation('spontaneous-candidates-page');
  const restaurantInfo = useEmployerRestaurantNames();
  const [restaurantFilter, setRestaurantFilter] = useAtom(employerSpontaneousCandidatesRestaurantFilterAtom);
  const router = useV2Router();
  const dropdownList = useMemo(
    () => restaurantInfo?.data?.map((restaurant) => restaurant.restaurant_name),
    [restaurantInfo?.data]
  );

  const canShowFilter = useMemo(
    () => restaurantInfo?.data?.length >= RESTAURANT_FILTER_VISIBILITY_THRESHOLD,
    [restaurantInfo?.data?.length]
  );

  const extendedDropdownList = useMemo(
    () => (dropdownList ? [`--- ${t('all_restaurants')} ---`, ...dropdownList] : []),
    [dropdownList, t]
  );

  const handleChange = useCallback(
    (name: string) => {
      const value = restaurantInfo?.data?.find((restaurant) => restaurant.restaurant_name === name);
      setRestaurantFilter(value);
      router.push(
        urlObjectToUriString({
          pathname: router.pathname,
          query: {
            ...(value?.id ? router.query : omitBy(router.query, (_, key) => key === 'restaurant_id')),
            ...(value?.id ? { restaurant_id: value?.id } : {}),
          },
        }),
        undefined,
        { shallow: true }
      );
    },
    [restaurantInfo?.data, router, setRestaurantFilter]
  );

  if (!canShowFilter) return null;

  return (
    <Combobox
      value={restaurantFilter?.restaurant_name || ''}
      onChange={handleChange}
      dropdownList={extendedDropdownList}
      placeholder={t('filter_by_restaurant')}
    />
  );
}
